import React, { useState } from 'react';
import { useReview } from '../../contexts/ReviewContext';
import { 
  Star, 
  ThumbsUp, 
  MessageCircle, 
  CheckCircle, 
  MoreVertical,
  Flag
} from 'lucide-react';

const ReviewCard = ({ review, onReply }) => {
  const { markReviewHelpful, getCommentsByReview } = useReview();
  const [showComments, setShowComments] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');

  const comments = getCommentsByReview(review.id);
  const hasComments = comments.length > 0;

  const handleHelpful = () => {
    markReviewHelpful(review.id);
  };

  const handleReply = () => {
    if (replyText.trim()) {
      // Ici on pourrait ajouter la logique pour ajouter un commentaire
      console.log('Reply:', replyText);
      setReplyText('');
      setShowReplyForm(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating 
            ? 'text-yellow-400 fill-current' 
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
      {/* Header de la review */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <img
            src={review.userAvatar}
            alt={review.userName}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-gray-900 dark:text-white">
                {review.userName}
              </h4>
              {review.verified && (
                <CheckCircle className="w-4 h-4 text-green-500" title="Achat vérifié" />
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex items-center gap-1">
                {renderStars(review.rating)}
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(review.date)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <Flag className="w-4 h-4" />
          </button>
          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <MoreVertical className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Contenu de la review */}
      <div className="mb-4">
        <h5 className="font-medium text-gray-900 dark:text-white mb-2">
          {review.title}
        </h5>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          {review.content}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <button
            onClick={handleHelpful}
            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary-400 transition-colors"
          >
            <ThumbsUp className="w-4 h-4" />
            <span>Utile ({review.helpful})</span>
          </button>
          
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary-400 transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Répondre ({comments.length})</span>
          </button>
        </div>
      </div>

      {/* Formulaire de réponse */}
      {showReplyForm && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Écrivez votre réponse..."
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-white resize-none"
            rows="3"
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => setShowReplyForm(false)}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Annuler
            </button>
            <button
              onClick={handleReply}
              className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
            >
              Répondre
            </button>
          </div>
        </div>
      )}

      {/* Commentaires */}
      {showComments && hasComments && (
        <div className="mt-4 space-y-3">
          <h6 className="font-medium text-gray-900 dark:text-white text-sm">
            Réponses ({comments.length})
          </h6>
          {comments.map((comment) => (
            <div key={comment.id} className="flex gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <img
                src={comment.userAvatar}
                alt={comment.userName}
                className="w-8 h-8 rounded-full object-cover"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-gray-900 dark:text-white">
                    {comment.userName}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(comment.date)}
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {comment.content}
                </p>
                <button className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary-400 mt-1">
                  J'aime ({comment.likes})
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bouton pour ajouter une réponse */}
      {showComments && !showReplyForm && (
        <div className="mt-3">
          <button
            onClick={() => setShowReplyForm(true)}
            className="text-sm text-primary dark:text-primary-400 hover:text-primary/80 dark:hover:text-primary-300"
          >
            Ajouter une réponse...
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewCard;
